/**
 * RTP↔UTC time mapping shared by the WebRTC players (camera view, layouts).
 *
 * The server pushes sync points over the data channel:
 * `{deviceId?, timestampUs, rtpTimestamp}` — collect them per device as
 * `{rtpTs, utcUs}` (newest last) and map a frame's 32-bit RTP timestamp to
 * absolute UTC ms against the newest point, wrap-aware.
 */

import { MILLISECOND_US, SECOND_MS } from '../timeUnits';
import type { SyncPoint } from '@/types/player';

export const kMaxSyncEntries = 8;

/** The RTP clock of video. */
export const RTP_VIDEO_CLOCK_HZ = 90_000;

const RTP_VIDEO_TICKS_PER_MS = RTP_VIDEO_CLOCK_HZ / SECOND_MS;

/** Maps a frame's RTP timestamp to epoch ms via the newest sync point (0 = no map yet). */
export function convertRtpToUtcMs(rtpTs: number, syncMap: readonly SyncPoint[]): number {
    const lastSyncPoint = syncMap?.[syncMap.length - 1];
    if (lastSyncPoint === undefined) { return 0; }
    // JS bitwise coerces to signed 32-bit, so this is already the wrap-aware shortest-path delta
    // in RTP ticks (negative = the frame sits before the sync point). 90 kHz ticks → microseconds.
    const diff = (rtpTs - lastSyncPoint.rtpTs) | 0;
    const deltaUs = diff * MILLISECOND_US / RTP_VIDEO_TICKS_PER_MS;
    return Math.trunc((lastSyncPoint.utcUs + deltaUs) / MILLISECOND_US);
}
